
import { useState } from 'react';
import { useToast } from "@/hooks/use-toast";
import { type Gift, type MarketAction, type MarketTransaction, type Brand } from '@/utils/types';

interface UseMarketActionsProps {
  gifts: Gift[];
  brands: Brand[];
  setGifts: (gifts: Gift[]) => void;
  transactions: MarketTransaction[];
  setTransactions: (transactions: MarketTransaction[]) => void;
  selectedGift: Gift | null;
  setSelectedGift: (gift: Gift | null) => void;
}

const getSuccessMessage = (action: MarketAction): string => {
  switch (action) {
    case 'consume':
      return "Gift card marked as used!";
    case 'exchange':
      return "Gift card exchanged successfully!";
    case 'regift':
      return "Gift card regifted!";
    case 'convert':
      return "Gift card converted!";
    default:
      return "Action completed successfully!";
  }
};

// Named export for useMarketActions
export const useMarketActions = ({
  gifts,
  brands,
  setGifts,
  transactions,
  setTransactions,
  selectedGift,
  setSelectedGift
}: UseMarketActionsProps) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const { toast } = useToast();
  
  const handleTransactionAction = async (
    action: MarketAction,
    selectedGift: Gift,
    options?: {
      targetGiftId?: string;
      targetUserId?: string;
      amount?: number;
      conversionAmount?: number;
    }
  ) => {
    setIsProcessing(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Create a transaction record
      const transaction: MarketTransaction = {
        id: `transaction-${Math.random().toString(36).substring(2, 9)}`,
        userId: 'current-user',
        giftId: selectedGift.id,
        action,
        targetGiftId: options?.targetGiftId,
        targetUserId: options?.targetUserId,
        amount: options?.amount || selectedGift.amount,
        createdAt: new Date(),
        status: 'completed'
      };
      
      // Update the gift in state based on the action
      let updatedGifts = [...gifts];
      
      // Handle different actions
      if (action === 'consume') {
        // Mark the gift as redeemed
        updatedGifts = updatedGifts.map(gift => 
          gift.id === selectedGift.id 
            ? { ...gift, isRedeemed: true } 
            : gift
        );
      } else if (action === 'exchange') {
        // For exchange, mark the current gift as redeemed and add a new one
        updatedGifts = updatedGifts.map(gift => 
          gift.id === selectedGift.id 
            ? { ...gift, isRedeemed: true } 
            : gift
        );
        
        // Add the new gift if exchange
        if (options?.targetGiftId) {
          const targetBrand = brands.find(b => b.id === options.targetGiftId);
          if (targetBrand) {
            const newGift: Gift = {
              id: `gift-${Math.random().toString(36).substring(2, 9)}`,
              brandId: targetBrand.id,
              amount: options.amount || selectedGift.amount,
              senderId: 'exchange-system',
              recipientId: 'current-user',
              createdAt: new Date(),
              isRedeemed: false
            };
            updatedGifts.push(newGift);
          }
        }
      }
      
      // Update state
      setGifts(updatedGifts);
      setSelectedGift(null);
      
      // Fixed type error by creating a new array explicitly with the correct type
      const updatedTransactions: MarketTransaction[] = [...transactions, transaction];
      setTransactions(updatedTransactions);
      
      toast({
        title: "Success!",
        description: getSuccessMessage(action),
      });
      
      return true;
    } catch (error) {
      console.error('Error processing transaction:', error);
      toast({
        title: "Error",
        description: "Failed to process the transaction. Please try again.",
        variant: "destructive",
      });
      return false;
    } finally {
      setIsProcessing(false);
    }
  };

  const handleConsume = async (gift: Gift) => {
    return handleTransactionAction('consume', gift);
  };

  const handleExchange = async (gift: Gift, targetGiftId: string, amount?: number) => {
    return handleTransactionAction('exchange', gift, { targetGiftId, amount });
  };

  const handleRegift = async (gift: Gift, targetUserId: string) => {
    return handleTransactionAction('regift', gift, { targetUserId });
  };

  const handleConvert = async (gift: Gift, conversionAmount: number) => {
    return handleTransactionAction('convert', gift, { amount: conversionAmount });
  };

  return {
    isProcessing,
    handleConsume,
    handleExchange,
    handleRegift,
    handleConvert
  };
};

// Also providing a default export for backward compatibility
export default useMarketActions;
